import { useState } from 'react';
import { ChevronRight, MessageSquarePlus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  REMARK_PLUGINS,
  REHYPE_PLUGINS,
  MARKDOWN_COMPONENTS,
} from '@/components/core/markdown/markdown';
import type { ClassifiedEntry } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';
import { previewText } from '@/lib/markdown/previewText';
import { cn } from '@/lib/utils';

// Re-export so existing imports referencing this module still resolve.
export type { ClassifiedEntry } from '@/lib/api-client';

interface QACardProps {
  entry: ClassifiedEntry;
  /** Continue the thread in the chat. Omitted: no follow-up action. */
  onFollowUp?: (entry: ClassifiedEntry) => void;
}

/** "Mar 8 · 2 sources" - when it was asked, and how grounded the answer is. */
function metaLine(entry: ClassifiedEntry): string {
  const date = parseUtcDate(entry.timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const n = entry.sources_used?.length ?? 0;
  return n > 0 ? `${date} · ${n} source${n === 1 ? '' : 's'}` : date;
}

/**
 * One question from the learner's chats, with its answer.
 *
 * The answer is the point, so it is never fully hidden: collapsed, the card
 * shows the first two lines of it (plain text - `previewText`) under the
 * question; opened, the whole rendered answer, and a way to keep going
 * ("Ask a follow-up"), instead of a dead end. The chevron sits at the right
 * edge so the question gets the full width.
 *
 * A bordered card from `sm`; below it, a hairline-divided row on the
 * lesson's paper.
 */
export function QACard({ entry, onFollowUp }: QACardProps) {
  const [expanded, setExpanded] = useState(false);
  const preview = previewText(entry.answer ?? '');

  return (
    <div className='rounded-lg border border-border/60 bg-card max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent'>
      {/* `aria-expanded`: the chevron's turn is otherwise the only statement
          of open/closed, which a screen reader cannot see. */}
      <button
        type='button'
        aria-expanded={expanded}
        onClick={() => setExpanded(v => !v)}
        className='flex w-full items-start gap-3 px-3 py-3 text-left max-sm:px-0'
      >
        <span className='min-w-0 flex-1'>
          <span className='block text-[15px] font-medium leading-snug text-foreground sm:text-sm'>
            {entry.question}
          </span>
          {!expanded && preview && (
            <span className='mt-1 line-clamp-2 block text-sm leading-relaxed text-muted-foreground'>
              {preview}
            </span>
          )}
          <span className='mt-1.5 block font-mono text-xs text-muted-foreground'>
            {metaLine(entry)}
          </span>
        </span>
        <ChevronRight
          className={cn(
            'mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-90'
          )}
          aria-hidden='true'
        />
      </button>

      {expanded && entry.answer && (
        <div className='px-3 pb-3 max-sm:px-0'>
          <div className='prose prose-sm max-w-none text-foreground dark:prose-invert'>
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={MARKDOWN_COMPONENTS}
            >
              {entry.answer}
            </ReactMarkdown>
          </div>
          {onFollowUp && (
            <button
              type='button'
              onClick={() => onFollowUp(entry)}
              className='mt-3 inline-flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-9'
            >
              <MessageSquarePlus className='h-4 w-4' aria-hidden='true' />
              Ask a follow-up
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ClassifiedListProps {
  entries: ClassifiedEntry[];
  icon: React.ElementType;
  emptyTitle: string;
  emptyDescription: string;
  /** Continue a thread in the chat - shown on an opened card. */
  onFollowUp?: (entry: ClassifiedEntry) => void;
  /** A line above the list naming what it is, e.g. "2 doubts from your
   *  chats". Omitted: no line. */
  countLabel?: (count: number) => string;
}

export function ClassifiedList({
  entries,
  icon: Icon,
  emptyTitle,
  emptyDescription,
  onFollowUp,
  countLabel,
}: ClassifiedListProps) {
  if (entries.length === 0) {
    // Outer is static `h-full` so we don't push past the parent
    // (which has `overflow-y-auto` and would flash a scrollbar during
    // the translateY start of the entry animation). The inner card
    // does the animating - its `translateY(20px)` start is purely
    // within the outer's bounds.
    return (
      <div className='flex h-full flex-col items-center justify-center px-6 py-12'>
        <div className='animate-fade-in-up text-center'>
          <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
            <Icon className='h-7 w-7 text-brand-500/80 dark:text-primary' />
          </div>
          <h3 className='mt-4 text-base font-medium text-foreground'>
            {emptyTitle}
          </h3>
          <p className='mt-1 max-w-sm text-sm text-muted-foreground'>
            {emptyDescription}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='p-3 max-sm:px-4'>
      {countLabel && (
        <p className='mb-2 px-1 text-xs text-muted-foreground max-sm:px-0'>
          {countLabel(entries.length)}
        </p>
      )}
      {/* Below `sm`: hairline-divided rows on the lesson's paper. */}
      <div className='space-y-2 max-sm:space-y-0 max-sm:divide-y max-sm:divide-border'>
        {entries.map(entry => (
          <QACard key={entry.id} entry={entry} onFollowUp={onFollowUp} />
        ))}
      </div>
    </div>
  );
}
